import { Voucher } from '../../../pages/reporting/vouchers/types'
import { VariableType } from '../../../components/Table/types'
import { DisplayScale } from '../../../shared/utils/numberScale'
import { formatVariableValue } from '../../../pages/reporting/custom/components/grid/utils/valueFormatting'
import {
  Match,
  MatchClassification,
  ReconciliationApprovalItem,
  ReconciliationCounterpartyGroup,
  VoucherIdentity
} from '../types'

const AMOUNT_FORMAT = { decimals: 2, displayScale: DisplayScale.default }

// "10884" -> "10 884,00". Blank for 0/undefined (formatVariableValue returns '' for falsy).
export const formatAmount = (value: number | undefined) =>
  value ? formatVariableValue(value, AMOUNT_FORMAT, VariableType.absolute) : ''

// As formatAmount but with the ISO currency code appended ("10 884,00 EUR"). The code is added
// here rather than passed to formatVariableValue, which would render a narrow symbol (€ / kr).
export const formatAmountWithCurrency = (value: number | undefined, currency: string) =>
  value ? `${formatAmount(value)} ${currency}` : ''

// A Match has no id of its own — its identity is the pair of voucher identities
// (companyId + guid) of whichever sides are present. Stable across refetches, so it
// doubles as the ag-grid row id.
const voucherKey = (voucher?: Voucher) => (voucher ? `${voucher.companyId}:${voucher.guid}` : '-')

export const getMatchId = (match: Match) => `${voucherKey(match.a)}|${voucherKey(match.b)}`

const sideIdentity = (voucher?: Voucher): VoucherIdentity | undefined =>
  voucher ? { companyId: voucher.companyId, voucherGuid: String(voucher.guid) } : undefined

// Build the POST /approvals item for a Match — only the voucher identities (+ optional
// comment); the server derives and stores everything else. Omit a side that has no voucher.
export const getApprovalItem = (match: Match, comment?: string): ReconciliationApprovalItem => {
  const a = sideIdentity(match.a)
  const b = sideIdentity(match.b)
  return {
    ...(a ? { a } : {}),
    ...(b ? { b } : {}),
    ...(comment ? { comment } : {})
  }
}

// Acceptance is a separate field by design — never derive it from classifications.
// A stale acceptance has been voided by a data change: the entry is open again.
export const isAcceptanceStale = (match: Match) => !!match.acceptanceStale

export const isAccepted = (match: Match) => !!match.accepted && !match.acceptanceStale

export type AcceptanceStatus = 'open' | 'accepted' | 'stale'

// Manual-acceptance state of the entry, for the "Käsittely" column. Kept separate from
// classifications by design (see note above).
export const getAcceptanceStatus = (match: Match): AcceptanceStatus => {
  if (isAcceptanceStale(match)) return 'stale'
  if (isAccepted(match)) return 'accepted'
  return 'open'
}

export const getClassifications = (match: Match): MatchClassification[] =>
  match.classifications && match.classifications.length > 0
    ? match.classifications
    : [MatchClassification.NO_COUNTERPART]

// Higher = more severe. Unknown values (backend added a new one) rank just above VALID so
// they still stand out without claiming to be the worst.
const CLASSIFICATION_SEVERITY: Record<MatchClassification, number> = {
  [MatchClassification.VALID]: 0,
  [MatchClassification.TIMING_DIFFERENCE]: 1,
  // FX-driven difference — an explainable discrepancy, ranked below a genuine sum mismatch
  [MatchClassification.CURRENCY_DIFFERENCE]: 1,
  [MatchClassification.SUM_DIFFERENCE]: 2,
  [MatchClassification.DUPLICATE_REFERENCE]: 3,
  [MatchClassification.EXTERNAL_ON_IC_ACCOUNT]: 4,
  [MatchClassification.NO_COUNTERPART]: 5,
  [MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT]: 6,
  [MatchClassification.INVALID_ASSETS_ACCOUNT]: 6,
  [MatchClassification.INVALID_LIABILITIES_ACCOUNT]: 6,
  [MatchClassification.INVALID_ACCOUNT]: 6
}

const severityOf = (classification: MatchClassification) => CLASSIFICATION_SEVERITY[classification] ?? 0.5

// The single most severe classification on the entry — for the one-dot status column.
export const getWorstClassification = (match: Match): MatchClassification =>
  getClassifications(match).reduce((worst, current) => (severityOf(current) > severityOf(worst) ? current : worst))

type VoucherSide = 'debit' | 'credit'

// Either side may be missing a voucher — treat the missing side's amount as 0.
// The matching side of b (debit vs credit) isn't fixed — check both pairings and use
// whichever is NOT closer to zero, since the near-zero one is coincidental and understates
// (or hides) the real discrepancy for a row that isn't a full match.
const pickSides = (match: Match): { aSide: VoucherSide; bSide: VoucherSide } => {
  const debitCreditSpread = Math.abs((match.a?.debit ?? 0) - (match.b?.credit ?? 0))
  const creditDebitSpread = Math.abs((match.a?.credit ?? 0) - (match.b?.debit ?? 0))
  return creditDebitSpread > debitCreditSpread
    ? { aSide: 'credit', bSide: 'debit' }
    : { aSide: 'debit', bSide: 'credit' }
}

export const getAmountA = (match: Match) => match.a?.[pickSides(match).aSide] ?? 0

export const getAmountB = (match: Match) => match.b?.[pickSides(match).bSide] ?? 0

export const getDifference = (match: Match) => getAmountA(match) - getAmountB(match)

const LEDGER_FIELD: Record<VoucherSide, 'ledgerDebit' | 'ledgerCredit'> = {
  debit: 'ledgerDebit',
  credit: 'ledgerCredit'
}

// Original ledger-currency amount for each side, using the same debit/credit choice as
// getAmountA/getAmountB so the figure lines up with the reporting-currency amount.
// undefined when the voucher carries no ledger-currency amount (booked in reporting currency).
export const getLedgerAmountA = (match: Match) => match.a?.[LEDGER_FIELD[pickSides(match).aSide]]

export const getLedgerAmountB = (match: Match) => match.b?.[LEDGER_FIELD[pickSides(match).bSide]]

export const getLedgerCurrencyA = (match: Match) => match.a?.ledgerCurrency

export const getLedgerCurrencyB = (match: Match) => match.b?.ledgerCurrency

// Effective currency of each present side: its ledger currency, or the reporting currency when
// it booked in that. undefined when the side has no voucher.
export const getSideCurrencies = (match: Match, groupCurrency: string): { a?: string; b?: string } => ({
  a: match.a ? match.a.ledgerCurrency ?? groupCurrency : undefined,
  b: match.b ? match.b.ledgerCurrency ?? groupCurrency : undefined
})

// Distinct side currencies in A→B order — one entry when they match (or one side is missing),
// two when they differ. Feeds the "Valuutta" column's value getter / set filter.
export const getMatchCurrencies = (match: Match, groupCurrency: string): string[] => {
  const { a, b } = getSideCurrencies(match, groupCurrency)
  return Array.from(new Set([a, b].filter((c): c is string => !!c)))
}

export const getCurrencyLabel = (match: Match, groupCurrency: string) =>
  getMatchCurrencies(match, groupCurrency).join(' / ')

// Only the invoice reference number of either side — never the free-text voucher
// description. Empty/whitespace counts as no reference.
export const getReference = (match: Match) =>
  match.a?.invoice?.referenceNumber?.trim() || match.b?.invoice?.referenceNumber?.trim() || undefined

// a is the AR/debit side (sale), b is the AP/credit side (purchase) — see pickAmountPair above.
export const getSalesInvoiceNumber = (match: Match) => match.a?.invoice?.number

export const getPurchaseInvoiceNumber = (match: Match) => match.b?.invoice?.number

// Whether the purchase-side (b) invoice has been paid — undefined when there's no b/invoice at all.
export const isPurchaseInvoicePaid = (match: Match): boolean | undefined => match.b?.invoice?.paid

export type PaymentStatus = 'paid' | 'open' | 'noInvoice' | 'noVoucher'

// Payment state of one side's invoice, for the "Tila A / Tila B" column.
export const getSidePaymentStatus = (voucher?: { invoice?: { paid: boolean } }): PaymentStatus => {
  if (!voucher) return 'noVoucher'
  if (!voucher.invoice) return 'noInvoice'
  return voucher.invoice.paid ? 'paid' : 'open'
}

export const getDate = (match: Match) => match.a?.date ?? match.b?.date

// Booking (voucher) date of each side separately, for the "Kirjauspäivä A / B" columns.
export const getDateA = (match: Match) => match.a?.date

export const getDateB = (match: Match) => match.b?.date

// Name of `side`'s company. When that side has no voucher, fall back to the other side's
// invoice partner — in an intercompany transaction it references this same company.
const resolveCompanyName = (
  side: Voucher | undefined,
  otherSide: Voucher | undefined,
  companyNameById: Map<string, string>
) => {
  if (side?.companyId) return companyNameById.get(side.companyId) ?? side.companyId
  const partner = otherSide?.invoice?.partner
  return partner ? companyNameById.get(partner.id ?? '') ?? partner.name : ''
}

export const getCompanyName = (match: Match, companyNameById: Map<string, string>) =>
  resolveCompanyName(match.a, match.b, companyNameById)

export const getCounterpartyName = (match: Match, companyNameById: Map<string, string>) =>
  resolveCompanyName(match.b, match.a, companyNameById)

export const buildCounterpartyGroups = (
  match: Match,
  companyNameById: Map<string, string>
): ReconciliationCounterpartyGroup[] => {
  const groups: ReconciliationCounterpartyGroup[] = []
  if (match.a) {
    groups.push({ companyName: getCompanyName(match, companyNameById), voucher: match.a })
  }
  if (match.b) {
    groups.push({ companyName: getCounterpartyName(match, companyNameById), voucher: match.b })
  }
  return groups
}
